import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Dividende {
    id: number;
    societe_symbole: string;
    date_paiement?: string | null;
    montant_net: number;
    montant_brut?: number | null;
    exercice?: number | null;
    statut: string;
    date_annonce?: string | null;
    date_detachement?: string | null;
}

interface DividendeCreate {
    societe_symbole: string;
    date_paiement: string;  // vide = non renseigné
    montant_net: number | string;
    montant_brut: number | string;
    exercice: number | string;
    statut: string;
    date_annonce: string;
    date_detachement: string;
}

interface Stock {
    symbole: string;
    nom_societe?: string;
}

const EMPTY_CREATE: DividendeCreate = {
    societe_symbole: '',
    date_paiement: '',
    montant_net: '',
    montant_brut: '',
    exercice: new Date().getFullYear() - 1,  // ex: en 2026 on saisit les dividendes de l'exercice 2025
    statut: 'ANNONCE',
    date_annonce: '',
    date_detachement: '',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'var(--bg-input, white)',
    boxSizing: 'border-box',
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </label>
        {children}
    </div>
);

const STATUT_STYLES: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    PAYE: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7', label: 'Payé' },
    ANNONCE: { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d', label: 'Annoncé' },
    AUCUN: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', label: 'Aucun dividende' },
};

const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
    const style = STATUT_STYLES[statut] || STATUT_STYLES.ANNONCE;
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            backgroundColor: style.bg,
            color: style.fg,
            border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

const AdminDividendsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [dividends, setDividends] = useState<Dividende[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchSymbol, setSearchSymbol] = useState('');
    const [searchedSymbol, setSearchedSymbol] = useState('');

    const [editingDividend, setEditingDividend] = useState<Dividende | null>(null);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newDividend, setNewDividend] = useState<DividendeCreate>(EMPTY_CREATE);
    const [addError, setAddError] = useState<string>('');
    const [addLoading, setAddLoading] = useState(false);

    const [showAucunForm, setShowAucunForm] = useState(false);
    const [aucunExercice, setAucunExercice] = useState<number | string>(new Date().getFullYear() - 1);
    const [aucunDateAnnonce, setAucunDateAnnonce] = useState('');
    const [aucunError, setAucunError] = useState<string>('');
    const [aucunLoading, setAucunLoading] = useState(false);

    const [creditingId, setCreditingId] = useState<number | null>(null);
    const [creditMessages, setCreditMessages] = useState<Record<number, string>>({});

    const fetchStocks = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/stocks/active`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setStocks(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching stocks:', error);
        }
    }, [token]);

    const fetchDividends = useCallback(async (symbole: string) => {
        if (!token || !symbole) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setDividends(Array.isArray(data) ? data : []);
                setSearchedSymbol(symbole);
            } else {
                setDividends([]);
            }
        } catch (error) {
            console.error('Error fetching dividends:', error);
            setDividends([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchStocks();
        }
    }, [token, hasPermission, fetchStocks]);

    const handleStockChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const symbol = e.target.value;
        setSearchSymbol(symbol);
        if (symbol) {
            fetchDividends(symbol);
            setNewDividend(prev => ({ ...prev, societe_symbole: symbol }));
        } else {
            setDividends([]);
            setSearchedSymbol('');
        }
    };

    const handleEdit = (dividend: Dividende) => {
        setEditingDividend({ ...dividend });
    };

    const handleCreditHolders = async (dividendId: number) => {
        setCreditingId(dividendId);
        setCreditMessages(prev => ({ ...prev, [dividendId]: '' }));
        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends/${dividendId}/credit-holders`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                const msg = data.already_credited
                    ? 'Déjà crédité précédemment (aucun doublon).'
                    : `✅ ${data.credited_count ?? 0} détenteur(s) crédité(s).`;
                setCreditMessages(prev => ({ ...prev, [dividendId]: msg }));
            } else {
                setCreditMessages(prev => ({ ...prev, [dividendId]: (data as { detail?: string }).detail || 'Échec du crédit.' }));
            }
        } catch (error) {
            setCreditMessages(prev => ({ ...prev, [dividendId]: 'Erreur réseau.' }));
        } finally {
            setCreditingId(null);
        }
    };

    const handleSave = async () => {
        if (!editingDividend) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends/${editingDividend.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    montant_net: Number(editingDividend.montant_net),
                    montant_brut: editingDividend.montant_brut !== undefined && editingDividend.montant_brut !== null ? Number(editingDividend.montant_brut) : null,
                    date_paiement: editingDividend.date_paiement || null,
                    exercice: editingDividend.exercice ? Number(editingDividend.exercice) : null,
                    statut: editingDividend.statut,
                    date_annonce: editingDividend.date_annonce || null,
                    date_detachement: editingDividend.date_detachement || null,
                }),
            });
            if (response.ok) {
                fetchDividends(searchedSymbol);
                setEditingDividend(null);
            } else {
                alert('Erreur lors de la mise à jour');
            }
        } catch (error) {
            console.error('Error updating dividend:', error);
        }
    };

    const handleDelete = async (dividend: Dividende) => {
        const confirmMsg = `Êtes-vous sûr de vouloir supprimer définitivement le dividende de ${dividend.societe_symbole} (Exercice ${dividend.exercice ?? 'N/A'}) ?`;
        if (!window.confirm(confirmMsg)) return;

        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends/${dividend.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                fetchDividends(searchedSymbol);
            } else {
                const data = await response.json().catch(() => ({}));
                alert((data as { detail?: string }).detail || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Error deleting dividend:', error);
            alert('Erreur réseau lors de la suppression');
        }
    };

    const handleAdd = async () => {
        setAddError('');
        if (!newDividend.societe_symbole || newDividend.montant_net === '') {
            setAddError('Les champs Société et Montant Net sont obligatoires.');
            return;
        }
        // date_paiement absent : exercice requis pour éviter les doublons
        if (!newDividend.date_paiement && !newDividend.exercice) {
            setAddError('Sans date de paiement, l’Exercice est obligatoire.');
            return;
        }
        setAddLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    societe_symbole: newDividend.societe_symbole,
                    date_paiement: newDividend.date_paiement || null,
                    montant_net: Number(newDividend.montant_net),
                    montant_brut: newDividend.montant_brut !== '' && newDividend.montant_brut !== undefined && newDividend.montant_brut !== null ? Number(newDividend.montant_brut) : null,
                    exercice: newDividend.exercice ? Number(newDividend.exercice) : null,
                    statut: newDividend.statut,
                    date_annonce: newDividend.date_annonce || null,
                    date_detachement: newDividend.date_detachement || null,
                }),
            });
            if (response.ok) {
                setShowAddForm(false);
                setNewDividend({ ...EMPTY_CREATE, societe_symbole: searchedSymbol });
                if (searchedSymbol) fetchDividends(searchedSymbol);
            } else {
                const err = await response.json().catch(() => ({}));
                setAddError((err as { detail?: string }).detail || 'Erreur lors de la création');
            }
        } catch (error) {
            setAddError('Erreur réseau');
        } finally {
            setAddLoading(false);
        }
    };

    const handleAddAucun = async () => {
        setAucunError('');
        if (!searchedSymbol || !aucunExercice) {
            setAucunError('L’Exercice est obligatoire.');
            return;
        }
        setAucunLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/dividends`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    societe_symbole: searchedSymbol,
                    date_paiement: null,
                    montant_net: 0,
                    montant_brut: 0,
                    exercice: Number(aucunExercice),
                    statut: 'AUCUN',
                    date_annonce: aucunDateAnnonce || null,
                    date_detachement: null,
                }),
            });
            if (response.ok) {
                setShowAucunForm(false);
                setAucunDateAnnonce('');
                fetchDividends(searchedSymbol);
            } else {
                const err = await response.json().catch(() => ({}));
                setAucunError((err as { detail?: string }).detail || 'Erreur lors de l’enregistrement');
            }
        } catch (error) {
            setAucunError('Erreur réseau');
        } finally {
            setAucunLoading(false);
        }
    };

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='payments' title='Gestion des Dividendes' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Recherche par Société</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                        <select
                            value={searchSymbol}
                            onChange={handleStockChange}
                            style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', maxWidth: '350px', fontSize: '14px', backgroundColor: 'white' }}
                        >
                            <option value="">-- Sélectionner une société --</option>
                            {stocks.map(stock => (
                                <option key={stock.symbole} value={stock.symbole}>
                                    {stock.symbole} - {stock.nom_societe || ''}
                                </option>
                            ))}
                        </select>

                        {searchedSymbol && (
                            <button
                                onClick={() => { setShowAddForm(true); setNewDividend({ ...EMPTY_CREATE, societe_symbole: searchedSymbol }); setAddError(''); }}
                                style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                + Ajouter un dividende annoncé
                            </button>
                        )}

                        {searchedSymbol && (
                            <button
                                onClick={() => { setShowAucunForm(true); setAucunExercice(new Date().getFullYear() - 1); setAucunDateAnnonce(''); setAucunError(''); }}
                                style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                Pas de dividende cette année
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <>
                            {searchedSymbol && <h3 style={{ marginBottom: '10px' }}>Historique pour {searchedSymbol}</h3>}
                            <div style={{ overflowX: 'auto' }}>
                                <table className='data-table' style={{ width: '100%' }}>
                                    <thead>
                                        <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Exercice</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date Annonce</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date Détachement</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date Paiement</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Montant Brut</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Montant Net</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                            <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dividends.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                    {searchedSymbol ? 'Aucun dividende trouvé' : 'Sélectionnez une société pour voir ses dividendes'}
                                                </td>
                                            </tr>
                                        ) : dividends.map((div) => (
                                            <React.Fragment key={div.id}>
                                            <tr style={{ backgroundColor: dividends.indexOf(div) % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                                <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px' }}>{div.exercice ?? '—'}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{div.date_annonce ?? '—'}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{div.date_detachement ?? '—'}</td>
                                                <td style={{ padding: '12px 16px' }}>{div.date_paiement ?? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Non fixée</span>}</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#334155' }}>
                                                    {div.statut === 'AUCUN' ? <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>—</span> : (div.montant_brut !== undefined && div.montant_brut !== null ? `${div.montant_brut} FCFA` : <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>—</span>)}
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                                                    {div.statut === 'AUCUN' ? <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>—</span> : `${div.montant_net} FCFA`}
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <StatutBadge statut={div.statut || 'PAYE'} />
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => handleEdit(div)}
                                                        style={{ backgroundColor: '#667eea', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '6px' }}
                                                    >
                                                        Modifier
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(div)}
                                                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: div.statut === 'PAYE' ? '6px' : 0 }}
                                                        title="Supprimer ce dividende"
                                                    >
                                                        Supprimer
                                                    </button>
                                                    {div.statut === 'PAYE' && (
                                                        <button
                                                            onClick={() => handleCreditHolders(div.id)}
                                                            disabled={creditingId === div.id}
                                                            title="Crédite les détenteurs actuels de cette action dans leur portefeuille virtuel — normalement automatique, à utiliser en cas d'échec ou pour vérifier."
                                                            style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
                                                        >
                                                            {creditingId === div.id ? 'Crédit...' : 'Créditer les détenteurs'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {creditMessages[div.id] && (
                                                <tr>
                                                    <td colSpan={8} style={{ padding: '4px 16px 12px 16px', fontSize: '12px', color: '#475569' }}>
                                                        {creditMessages[div.id]}
                                                    </td>
                                                </tr>
                                            )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </Box>

            {/* Modal Ajout */}
            {showAddForm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'var(--bg-box)', padding: '28px', borderRadius: '10px', width: '460px', maxWidth: '95vw' }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '16px', fontWeight: 700 }}>
                            Ajouter un dividende — {newDividend.societe_symbole}
                        </h3>

                        <FieldRow label="Société">
                            <select value={newDividend.societe_symbole} onChange={e => setNewDividend(p => ({ ...p, societe_symbole: e.target.value }))} style={inputStyle}>
                                <option value="">-- Sélectionner --</option>
                                {stocks.map(s => <option key={s.symbole} value={s.symbole}>{s.symbole} - {s.nom_societe || ''}</option>)}
                            </select>
                        </FieldRow>

                        <FieldRow label="Exercice">
                            <input type="number" placeholder="ex: 2025" value={newDividend.exercice} onChange={e => setNewDividend(p => ({ ...p, exercice: e.target.value }))} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Montant Brut (FCFA)">
                            <input type="number" step="0.001" placeholder="ex: 1933.33 (Optionnel)" value={newDividend.montant_brut} onChange={e => setNewDividend(p => ({ ...p, montant_brut: e.target.value }))} style={inputStyle} />
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Optionnel — montant brut avant IRVM (utilisé pour les valorisations si renseigné)</small>
                        </FieldRow>

                        <FieldRow label="Montant Net (FCFA) *">
                            <input type="number" step="0.001" placeholder="ex: 1740" value={newDividend.montant_net} onChange={e => setNewDividend(p => ({ ...p, montant_net: e.target.value }))} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date Annonce">
                            <input type="date" value={newDividend.date_annonce} onChange={e => setNewDividend(p => ({ ...p, date_annonce: e.target.value }))} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date Détachement">
                            <input type="date" value={newDividend.date_detachement} onChange={e => setNewDividend(p => ({ ...p, date_detachement: e.target.value }))} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date Paiement">
                            <input type="date" value={newDividend.date_paiement} onChange={e => setNewDividend(p => ({ ...p, date_paiement: e.target.value }))} style={inputStyle} />
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Optionnel — peut être renseigné ultérieurement</small>
                        </FieldRow>

                        <FieldRow label="Statut">
                            <select value={newDividend.statut} onChange={e => setNewDividend(p => ({ ...p, statut: e.target.value }))} style={inputStyle}>
                                <option value="ANNONCE">Annoncé</option>
                                <option value="PAYE">Payé</option>
                            </select>
                        </FieldRow>

                        {addError && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{addError}</p>}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                            <button onClick={() => { setShowAddForm(false); setAddError(''); }} className='button'>Annuler</button>
                            <button onClick={handleAdd} className='button button-purple' disabled={addLoading}>
                                {addLoading ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Pas de dividende (statut AUCUN) */}
            {showAucunForm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'var(--bg-box)', padding: '28px', borderRadius: '10px', width: '440px', maxWidth: '95vw' }}>
                        <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 700 }}>
                            Pas de dividende — {searchedSymbol}
                        </h3>

                        <p style={{
                            backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b',
                            padding: '10px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '18px', lineHeight: 1.4,
                        }}>
                            Vous enregistrez une décision de non-distribution de dividende. Ceci affectera le calcul
                            des modèles de valorisation (le dividende de cet exercice sera traité comme nul, sans
                            hériter du dividende de l'année précédente).
                        </p>

                        <FieldRow label="Exercice *">
                            <input type="number" placeholder="ex: 2025" value={aucunExercice} onChange={e => setAucunExercice(e.target.value)} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date de la décision AGO">
                            <input type="date" value={aucunDateAnnonce} onChange={e => setAucunDateAnnonce(e.target.value)} style={inputStyle} />
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Optionnel</small>
                        </FieldRow>

                        {aucunError && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{aucunError}</p>}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                            <button onClick={() => { setShowAucunForm(false); setAucunError(''); }} className='button'>Annuler</button>
                            <button
                                onClick={handleAddAucun}
                                disabled={aucunLoading}
                                style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                            >
                                {aucunLoading ? 'Enregistrement...' : 'Confirmer — Aucun dividende'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Modification */}
            {editingDividend && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'var(--bg-box)', padding: '24px', borderRadius: '8px', width: '460px', maxWidth: '95vw' }}>
                        <h3 style={{ marginBottom: '18px' }}>Modifier Dividende ({editingDividend.societe_symbole})</h3>

                        <FieldRow label="Date Paiement">
                            <input type='date' value={editingDividend.date_paiement ?? ''} onChange={e => setEditingDividend({ ...editingDividend, date_paiement: e.target.value || null })} style={inputStyle} />
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Laisser vide si la date n'est pas encore fixée</small>
                        </FieldRow>

                        <FieldRow label="Montant Brut (FCFA)">
                            <input
                                type='number'
                                step="0.001"
                                disabled={editingDividend.statut === 'AUCUN'}
                                placeholder="ex: 1933.33"
                                value={editingDividend.montant_brut !== undefined && editingDividend.montant_brut !== null ? editingDividend.montant_brut : ''}
                                onChange={e => setEditingDividend({ ...editingDividend, montant_brut: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                style={{ ...inputStyle, ...(editingDividend.statut === 'AUCUN' ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            />
                            <small style={{ color: '#94a3b8', fontSize: '11px' }}>Optionnel — montant brut avant IRVM (utilisé pour les valorisations)</small>
                        </FieldRow>

                        <FieldRow label="Montant Net (FCFA)">
                            <input
                                type='number'
                                step="0.001"
                                disabled={editingDividend.statut === 'AUCUN'}
                                value={editingDividend.montant_net !== undefined && editingDividend.montant_net !== null ? editingDividend.montant_net : ''}
                                onChange={e => setEditingDividend({ ...editingDividend, montant_net: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                style={{ ...inputStyle, ...(editingDividend.statut === 'AUCUN' ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            />
                            {editingDividend.statut === 'AUCUN' && (
                                <small style={{ color: '#94a3b8', fontSize: '11px' }}>Forcé à 0 — décision de non-distribution</small>
                            )}
                        </FieldRow>

                        <FieldRow label="Exercice">
                            <input type='number' value={editingDividend.exercice ?? ''} onChange={e => setEditingDividend({ ...editingDividend, exercice: Number(e.target.value) })} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date Annonce">
                            <input type='date' value={editingDividend.date_annonce ?? ''} onChange={e => setEditingDividend({ ...editingDividend, date_annonce: e.target.value })} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Date Détachement">
                            <input type='date' value={editingDividend.date_detachement ?? ''} onChange={e => setEditingDividend({ ...editingDividend, date_detachement: e.target.value })} style={inputStyle} />
                        </FieldRow>

                        <FieldRow label="Statut">
                            <select value={editingDividend.statut || 'PAYE'} onChange={e => setEditingDividend({ ...editingDividend, statut: e.target.value })} style={inputStyle}>
                                <option value="ANNONCE">Annoncé</option>
                                <option value="PAYE">Payé</option>
                                <option value="AUCUN">Aucun dividende</option>
                            </select>
                            {editingDividend.statut === 'AUCUN' && (
                                <small style={{ color: '#991b1b', fontSize: '11px' }}>
                                    Décision de non-distribution : affectera le calcul des modèles de valorisation.
                                </small>
                            )}
                        </FieldRow>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                            <button onClick={() => setEditingDividend(null)} className='button'>Annuler</button>
                            <button onClick={handleSave} className='button button-purple'>Enregistrer</button>
                        </div>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminDividendsScreen;


